import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Loader2, Mail } from 'lucide-react';
import { Badge, entityStateBadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Input';
import { PageSection } from '@/components/ui/PageSection';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrap,
} from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrencyValue, formatPercentValue } from '@/api/keywordMetrics';
import { adsApi } from '@/api/client';
import type {
  ProfileDailyExcelMetricKind,
  ReportCampaign,
  ReportSegment,
} from '@/types/profileDailyExcel';

const SEGMENT_OPTIONS: Array<{ id: ReportSegment; label: string; variant: 'success' | 'warning' | 'brand' }> = [
  { id: 'brand', label: 'Brand', variant: 'success' },
  { id: 'competitor', label: 'Comp', variant: 'warning' },
  { id: 'generic', label: 'Generic', variant: 'brand' },
];

function campaignKey(campaign: Pick<ReportCampaign, 'campaignType' | 'campaignId'>): string {
  return `${campaign.campaignType}:${campaign.campaignId}`;
}

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function formatMetric(kind: ProfileDailyExcelMetricKind, value: number | null, currencyCode: string): string {
  if (value == null) return '—';
  if (kind === 'int') return value.toLocaleString();
  if (kind === 'money') return formatCurrencyValue(value, { currencyCode });
  if (kind === 'pct') return formatPercentValue(value);
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function segmentLabel(segment: ReportSegment | null): string {
  if (segment === 'brand') return 'Brand';
  if (segment === 'competitor') return 'Comp';
  if (segment === 'generic') return 'Generic';
  return 'Untagged';
}

export function ReportPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('profiles.write');
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [adProduct, setAdProduct] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSelected(new Set());
    setSelectAllMatching(false);
  }, [search, segmentFilter, adProduct, profileId]);

  const configQuery = useQuery({
    queryKey: ['profile-daily-excel', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileDailyExcel(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load the report');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const previewQuery = useQuery({
    queryKey: ['profile-daily-excel-preview', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileDailyExcelPreview(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load the preview');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const campaignQuery = useQuery({
    queryKey: ['profile-daily-excel-campaigns', profileId, search, segmentFilter, adProduct],
    queryFn: async () => {
      const res = await adsApi.getReportCampaigns(profileId, {
        search: search || undefined,
        segment: segmentFilter === 'all' ? undefined : segmentFilter,
        adProduct: adProduct === 'all' ? undefined : adProduct,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load campaigns');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const campaigns = campaignQuery.data?.campaigns ?? [];
  const total = campaignQuery.data?.total ?? 0;
  const truncated = campaignQuery.data?.truncated ?? false;
  const loadedKeys = useMemo(() => campaigns.map(campaignKey), [campaigns]);
  const allLoadedSelected = loadedKeys.length > 0 && loadedKeys.every((key) => selected.has(key));
  const tagCount = selectAllMatching ? total : selected.size;

  const scheduleMutation = useMutation({
    mutationFn: async (scheduleEnabled: boolean) => {
      const res = await adsApi.updateProfileDailyExcelSchedule(profileId, scheduleEnabled);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to update the schedule');
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['profile-daily-excel', profileId], data);
      showToast(data.scheduleEnabled ? 'Daily email turned on' : 'Daily email turned off', 'success');
    },
    onError: (error: unknown) => showToast(errorMessage(error, 'Failed to update the schedule'), 'error'),
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.sendProfileDailyExcel(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to send the report');
      return res.data;
    },
    onSuccess: (data) => {
      showToast(`Report sent to ${data.recipientCount ?? 0} people`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['profile-daily-excel', profileId] });
    },
    onError: (error: unknown) => showToast(errorMessage(error, 'Failed to send the report'), 'error'),
  });

  const tagMutation = useMutation({
    mutationFn: async (segment: ReportSegment | null) => {
      const match = selectAllMatching
        ? {
            search: search || null,
            segment: segmentFilter === 'all' ? null : segmentFilter,
            adProduct: adProduct === 'all' ? null : adProduct,
          }
        : undefined;
      const campaignKeys = selectAllMatching
        ? undefined
        : campaigns
            .filter((campaign) => selected.has(campaignKey(campaign)))
            .map((campaign) => ({
              campaignId: campaign.campaignId,
              campaignType: campaign.campaignType,
            }));
      const res = await adsApi.updateReportCampaignSegments(profileId, {
        segment,
        campaignKeys,
        match,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to tag campaigns');
      return res.data;
    },
    onSuccess: (data) => {
      const label = data.segment ? segmentLabel(data.segment) : 'Untagged';
      showToast(`${data.updated} campaigns set to ${label}`, 'success');
      setSelected(new Set());
      setSelectAllMatching(false);
      void queryClient.invalidateQueries({ queryKey: ['profile-daily-excel-campaigns', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['profile-daily-excel-preview', profileId] });
    },
    onError: (error: unknown) => showToast(errorMessage(error, 'Failed to tag campaigns'), 'error'),
  });

  const config = configQuery.data;
  const preview = previewQuery.data;
  const currencyCode = preview?.currencyCode || config?.currencyCode || 'USD';
  const recipients = config?.recipients ?? [];

  const toggleLoaded = (checked: boolean) => {
    if (!checked) {
      setSelected(new Set());
      setSelectAllMatching(false);
      return;
    }
    setSelected(new Set(loadedKeys));
    setSelectAllMatching(truncated);
  };

  const toggleOne = (key: string, checked: boolean) => {
    setSelectAllMatching(false);
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 bg-page px-4 py-6 min-h-full">
      <div>
        <h1 className="text-headline-md font-semibold text-foreground">Report</h1>
        <p className="mt-1 text-sm text-foreground-muted">
          Four complete days ending yesterday
          {config?.timezone ? ` (${config.timezone})` : ''}. Headline metrics include every
          campaign. When only Brand is tagged, the sheet shows Brand and Rest, and Rest is every
          campaign that is not tagged Brand. When a campaign is tagged Comp or Generic, the sheet
          shows Brand, Comp, and Generic instead.
        </p>
      </div>

      <PageSection title="Delivery">
        <Card className="space-y-4 p-5">
          {configQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading delivery settings
            </div>
          ) : configQuery.error ? (
            <p className="text-sm text-error-ink">{errorMessage(configQuery.error, 'Failed to load the report')}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
                    Recipients
                  </p>
                  {recipients.length === 0 ? (
                    <p className="mt-2 text-sm text-foreground-muted">
                      Nobody with access to this account has an email address, so the report cannot be sent.
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-1">
                      {recipients.map((person) => (
                        <li key={person.email} className="text-sm text-foreground">
                          <span className="font-medium">{person.name}</span>
                          <span className="text-foreground-muted"> · {person.email}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  onClick={() => sendMutation.mutate()}
                  disabled={!canWrite || recipients.length === 0 || sendMutation.isPending}
                >
                  {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Send now
                </Button>
              </div>

              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-brand"
                  checked={Boolean(config?.scheduleEnabled)}
                  disabled={!canWrite || scheduleMutation.isPending}
                  onChange={(event) => scheduleMutation.mutate(event.target.checked)}
                />
                <span>
                  Email this report every day at 08:00
                  {config?.timezone ? ` ${config.timezone}` : ''}, for the four complete days ending yesterday.
                </span>
              </label>

              {config?.lastSend ? (
                <p className="text-sm text-foreground-muted">
                  Last {config.lastSend.source} send{' '}
                  {config.lastSend.status === 'sent' ? 'succeeded' : 'failed'}
                  {config.lastSend.sentAt
                    ? ` ${new Date(config.lastSend.sentAt).toLocaleString()}`
                    : ''}
                  {config.lastSend.status === 'sent'
                    ? ` to ${config.lastSend.recipientCount} ${config.lastSend.recipientCount === 1 ? 'person' : 'people'}`
                    : ''}
                  .
                </p>
              ) : (
                <p className="text-sm text-foreground-muted">This report has not been sent yet.</p>
              )}
              {!canWrite ? (
                <p className="text-sm text-foreground-muted">You can view this report. Changing it needs write access.</p>
              ) : null}
            </>
          )}
        </Card>
      </PageSection>

      <PageSection
        title="Preview"
        actions={
          config?.window ? (
            <span className="text-sm text-foreground-muted">
              {config.window.labels[0]} – {config.window.labels[config.window.labels.length - 1]}
            </span>
          ) : null
        }
      >
        <Card className="overflow-hidden">
          {previewQuery.isLoading ? (
            <div className="flex items-center gap-2 p-5 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Building the four-day grid
            </div>
          ) : previewQuery.error ? (
            <p className="p-5 text-sm text-error-ink">{errorMessage(previewQuery.error, 'Failed to load the preview')}</p>
          ) : (
            <TableWrap>
              <Table compact>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-40">Date</TableHead>
                    {(preview?.labels ?? []).map((label) => (
                      <TableHead key={label} className="text-right">
                        {label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <tbody>
                  {(preview?.rows ?? []).map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="sticky left-0 z-10 bg-surface font-medium">{row.label}</TableCell>
                      {row.values.map((value, index) => (
                        <TableCell key={`${row.key}-${preview?.dates[index] ?? index}`} className="text-right font-mono">
                          {formatMetric(row.kind, value, currencyCode)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
        <p className="text-sm text-foreground-muted">
          A day that is not in the imported report uses Marketing Stream. CTR is clicks divided by
          impressions, CVR is orders divided by clicks, and ROI is GMS divided by spend. Orders and
          GMS use 14-day attribution. With only Brand tagged, Rest is everything else, so Brand plus
          Rest matches Spends and GMS. With Comp or Generic tagged, those rows include only tagged
          campaigns, and untagged campaigns stay in the headline rows.
        </p>
      </PageSection>

      <PageSection title="Campaign tags">
        <Card className="space-y-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search campaigns"
              aria-label="Search campaigns"
              className="max-w-sm"
            />
            <Select
              aria-label="Segment"
              value={segmentFilter}
              onChange={(event) => setSegmentFilter(event.target.value)}
            >
              <option value="all">All segments</option>
              <option value="brand">Brand</option>
              <option value="competitor">Comp</option>
              <option value="generic">Generic</option>
              <option value="untagged">Untagged</option>
            </Select>
            <Select
              aria-label="Ad product"
              value={adProduct}
              onChange={(event) => setAdProduct(event.target.value)}
            >
              <option value="all">All products</option>
              <option value="SP">Sponsored Products</option>
              <option value="SB">Sponsored Brands</option>
              <option value="SD">Sponsored Display</option>
            </Select>
            <span className="text-sm text-foreground-muted">
              {total.toLocaleString()} {total === 1 ? 'campaign' : 'campaigns'}
            </span>
          </div>

          {tagCount > 0 && canWrite ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2">
              <span className="text-sm font-medium text-foreground">
                {selectAllMatching
                  ? `Tag all ${total.toLocaleString()} matching campaigns`
                  : `Tag ${tagCount.toLocaleString()} selected`}
              </span>
              {SEGMENT_OPTIONS.map((option) => (
                <Button
                  key={option.id}
                  size="sm"
                  variant="secondary"
                  disabled={tagMutation.isPending}
                  onClick={() => tagMutation.mutate(option.id)}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                disabled={tagMutation.isPending}
                onClick={() => tagMutation.mutate(null)}
              >
                Clear
              </Button>
            </div>
          ) : null}

          {truncated ? (
            <button
              type="button"
              className="text-sm font-medium text-brand-deep hover:underline"
              onClick={() => {
                setSelected(new Set(loadedKeys));
                setSelectAllMatching(true);
              }}
            >
              Select all {total.toLocaleString()} campaigns matching this search
            </button>
          ) : null}

          {campaignQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading campaigns
            </div>
          ) : campaignQuery.error ? (
            <p className="text-sm text-error-ink">{errorMessage(campaignQuery.error, 'Failed to load campaigns')}</p>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-foreground-muted">No campaigns match this search.</p>
          ) : (
            <TableWrap className="max-h-[32rem]">
              <Table compact>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all visible campaigns"
                        className="h-3.5 w-3.5 rounded border-border accent-brand"
                        checked={selectAllMatching || allLoadedSelected}
                        onChange={(event) => toggleLoaded(event.target.checked)}
                      />
                    </TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Tag</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {campaigns.map((campaign) => {
                    const key = campaignKey(campaign);
                    const option = SEGMENT_OPTIONS.find((item) => item.id === campaign.segment);
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label={`Select ${campaign.name || campaign.campaignId}`}
                            className="h-3.5 w-3.5 rounded border-border accent-brand"
                            checked={selectAllMatching || selected.has(key)}
                            onChange={(event) => toggleOne(key, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell className="max-w-md truncate font-medium">{campaign.name || campaign.campaignId}</TableCell>
                        <TableCell>{campaign.campaignType}</TableCell>
                        <TableCell>
                          {campaign.state ? (
                            <Badge variant={entityStateBadgeVariant(campaign.state)}>{campaign.state}</Badge>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {option ? (
                            <Badge variant={option.variant}>{option.label}</Badge>
                          ) : (
                            <span className="text-foreground-muted">Untagged</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </PageSection>
    </div>
  );
}
